import React, { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { Controller, ControllerProps, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

// Sentry
import * as Sentry from '@sentry/react';

// styled components
import {
  ContainerCampaign,
  ContainerDescription,
  ContainerSwitch,
  StyledTypography,
} from './styles';
import {
  Title,
  LocationInputGroupOrder,
  RoadInputSubGroup,
  InputTextAreaStyled,
  InputsContainer,
  ConcessionWrapper,
} from '../components/MainStyled';

// Components
import DropDownInput from '../../inputs/DropDown/DropDownInput';
import MultiSelectDropdown from '../../inputs/DropDown/MultiSelectDropdown';
import SearchableDropdown from '../../inputs/SearchableDropdown';
import { HelpTooltip } from '../../display/HelpTooltip';
import InputText from '../../inputs/InputText';
import { ArrowRadioGroup } from '../../../pages/main/comunications/NewComunication/styles';
import SimpleSwitch from '../../inputs/buttons/SwitchButton/Simple';

// Types
import { FormProps, SetValueType, GroupTypes } from './types';
import {
  Margin,
  WorkOrder,
  WorkOrderDirection,
} from '../../../types/workOrder';
import { ImperativeRefInput } from '../../inputs/DropDown/DropDownInput/types';
import { ImperativeRefSearchableDropdown } from '../../inputs/SearchableDropdown/types';
import { OptionSelected } from '../../inputs/DropDown/DropDownList/types';
import { type PartialDirection } from '../../inputs/buttons/ArrowDirectionButton/types';

// GQL
import {
  GET_CONCESSION_BY_ID,
  GET_ROAD_BY_ID,
  GET_ROADS_BY_IDS,
} from '../../../gql/queries/geoPositions/roads';
import {
  GET_CAPITOL_BY_ID,
  GET_CLASSIFICATION_BY_ID_OR_ROID,
  GET_ELEMENT_ORDERS_BY_ID,
  GET_ELEMENT_ORDER_BY_ID,
  GET_SUB_CAPITOLS_BY_IDS,
  GET_SUB_CAPITOL_BY_ID,
} from '../../../gql/queries/orders/predefinedData';
import { GET_ACTIVITY_BY_ID } from '../../../gql/queries/actions/activitys';
import { client } from '../../../gql/client';

// Media
import {
  useIsLandscapeTablet,
  useIsPortraitTablet,
} from '../../../hooks/useMediaBreakPoints';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';

// Auth
import { useAuth } from '../../../hooks/useAuth';

// Constants
import { formOrderNaming } from './constants';
import { formActionNaming } from '../ActionForms/constants';
import { formAccidentNaming } from '../AddBasicInfoAccidentForm/constants';

// Utils
import { pkFormatter } from '../../../utils/transform';
import { LONG_ORDER_SPAN_METERS, orderSpanMeters } from '../../../utils/pkValidation';
import { useLocation } from 'react-router-dom';
import { fetchDefaultData } from '../../../utils/fetchData';
import { groupLanes, groupTracks } from '../../../data/orders';
import {
  laneLabel,
  trackLabel,
  translateLaneOptions,
  translateTrackOptions,
  type CatalogEntry,
} from '../../../utils/catalogLabels';
import { UNSPECIFIC } from '../../../types/utils';
import { registerPk } from '../../../utils/pkField';
import type { PkCandidate } from '../../../utils/resolvePkAtPosition';

export const Description = ({
  register,
  control,
  errors,
  setValue,
  switchState,
  setSwitchState,
  namingPersistForm,
  isAccident,
  isAction,
  autoFillData,
  onRegisterAutolocate,
}: FormProps & SetValueType) => {
  const { t } = useTranslation('forms');
  // Feature flag para mostrar/ocultar campo margen
  const showMargin = useFeatureFlag('showMargin', true);

  // Hooks
  const subCapitolDropDownRef = useRef<ImperativeRefSearchableDropdown>(null);
  const roadDropDownRef = useRef<ImperativeRefInput>(null);
  const capitolDropDownRef = useRef<ImperativeRefSearchableDropdown>(null);
  const elementOrdersDropDownRef =
    useRef<ImperativeRefSearchableDropdown | null>(null);
  const activitysDropDownRef =
    useRef<ImperativeRefSearchableDropdown | null>(null);
  const classificationDropDownRef = useRef<ImperativeRefInput | null>(null);
  const concessionDropDownRef = useRef<ImperativeRefInput | null>(null);
  const trackDropDownRef = useRef<ImperativeRefInput | null>(null);
  const laneDropDownRef = useRef<ImperativeRefInput | null>(null);
  const predefineDropDownRef = useRef<ImperativeRefSearchableDropdown | null>(null);
  const isLandscapeTablet = useIsLandscapeTablet();
  const isPortraitTable = useIsPortraitTablet();
  const isEditForm = namingPersistForm === formOrderNaming.EDIT_CAMPAIGN_FORM;
  const isAnAction = namingPersistForm === formActionNaming.NEW_ACTION_DATA;
  const isAnAccident =
    namingPersistForm === formAccidentNaming.NEW_ACCIDENT_FORM ||
    namingPersistForm === formAccidentNaming.EDIT_ACCIDENT_FORM;

  const location = useLocation();

  // Watchers
  const subCapitols = useWatch({
    control,
    name: 'subCapitols',
  });

  const roads = useWatch({
    control,
    name: 'roads',
  });
  const city = useWatch({
    control,
    name: 'city',
  });
  const pkInitRoad = useWatch({
    control,
    name: 'pkInitRoad',
  });
  const pkEndRoad = useWatch({
    control,
    name: 'pkEndRoad',
  });

  const elementOrders = useWatch({
    control,
    name: 'elementOrders',
  });

  const roadDirections = useWatch({
    control,
    name: 'roadDirections',
  });

  const fetchToFillData = useCallback(async () => {
    try {
      const [
        {
          data: { capitol },
        },
        {
          data: { subCapitol },
        },
        {
          data: { classification },
        },
        {
          data: { concession },
        },
        {
          data: { road },
        },
      ] = await Promise.all([
        client.query({
          query: GET_CAPITOL_BY_ID,
          variables: {
            id: autoFillData?.order?.capitolIDs[0],
          },
        }),
        client.query({
          query: GET_SUB_CAPITOL_BY_ID,
          variables: {
            id: autoFillData?.order?.subCapitolIDs[0],
          },
        }),
        client.query({
          query: GET_CLASSIFICATION_BY_ID_OR_ROID,
          variables: {
            id: autoFillData?.order?.classificationIDs[0],
          },
        }),
        client.query({
          query: GET_CONCESSION_BY_ID,
          variables: {
            id: autoFillData?.genericOrder
              ? autoFillData?.genericOrder?.concessionIDs[0]
              : autoFillData?.campaignData?.concessionIDs[0],
          },
        }),
        client.query({
          query: GET_ROAD_BY_ID,
          variables: {
            id: autoFillData?.genericOrder
              ? autoFillData?.genericOrder?.roadIDs[0]
              : autoFillData?.campaignData?.roadIDs[0],
          },
        }),
      ]);

      // Spec docs/superpowers/specs/2026-10-29-autovinculo-lugar-impreciso-design.md
      // § 5 (autovínculo por lugar impreciso): si la OT padre es larga
      // (> 4 km, p. ej. una campaña de 47 km) NO se copia su rango de PK: el
      // trabajo no ocupa toda la OT y el rango entero rompe el autovínculo del
      // back y los informes. Sin PK, el esquema del paso 1 obliga a usar
      // «Autolocalizar PK» o a teclearlo.
      const pkSource = autoFillData?.genericOrder ?? autoFillData?.campaignData;
      if (pkSource && orderSpanMeters(pkSource) <= LONG_ORDER_SPAN_METERS) {
        setValue(
          'pkInit',
          `${pkSource.pkInitKm}+${String(pkSource.pkInitMeter).padStart(3, '0')}`
        );
        setValue(
          'pkEnd',
          `${pkSource.pkEndKm}+${String(pkSource.pkEndMeter).padStart(3, '0')}`
        );
      }

      autoFillData?.genericOrder?.direction
        ? setValue(
          'direction',
          autoFillData?.genericOrder?.direction as WorkOrderDirection
        )
        : setValue(
          'direction',
          autoFillData?.campaignData?.direction as WorkOrderDirection
        );

      autoFillData?.genericOrder?.margin
        ? setValue('margin', autoFillData?.genericOrder?.margin)
        : setValue(
          'margin',
          autoFillData?.campaignData?.margin as 'left' | 'right' | 'both'
        );

      capitolDropDownRef.current?.setSelectedOption({
        label: capitol.name,
        value: capitol.id,
        data: capitol.subCapitolsIDs,
      });

      subCapitolDropDownRef.current?.setSelectedOption({
        label: subCapitol.name,
        value: subCapitol.id,
        data: subCapitol.elementOrdersIds,
      });

      classificationDropDownRef.current?.setSelectedOption({
        label: classification.name,
        value: classification.id,
      });

      concessionDropDownRef.current?.setSelectedOption({
        label: concession.name,
        value: concession.id,
        data: concession.roadIDs,
      });

      roadDropDownRef.current?.setSelectedOption({
        label: road.name,
        value: road.id,
      });
      autoFillData?.genericOrder
        ? setValue('road', autoFillData.genericOrder.roadIDs[0])
        : setValue('road', autoFillData?.campaignData?.roadIDs[0] as string);
      setValue('roadDirections', road.directions);

      const findOptionTrack = groupTracks.find((track) =>
        autoFillData?.genericOrder
          ? track.value === autoFillData?.genericOrder?.track
          : track.value === autoFillData?.campaignData?.track
      );
      trackDropDownRef.current?.setSelectedOption({
        label: trackLabel(findOptionTrack?.value, t),
        value: findOptionTrack?.value as string,
      });

      const findOptionLane = groupLanes.find((lane) =>
        autoFillData?.genericOrder
          ? lane.value === autoFillData?.genericOrder?.lane
          : lane.value === autoFillData?.campaignData?.lane
      );
      laneDropDownRef.current?.setSelectedOption({
        label: laneLabel(findOptionLane?.value, t),
        value: findOptionLane?.value as string,
      });
      // Descripción: NO se autorrellena (decisión usuario 2026-08-07). El
      // "Autorrellenar" de Actuación/Accidente copia la ubicación y la
      // clasificación de la OT padre, pero la descripción es del trabajo
      // concreto — heredar la del padre obligaba a borrarla siempre.
      // Comunicaciones mantiene su propio autorrelleno (NewCommunicationForm).

      // Auto-select predefinido si existe
      if (autoFillData?.order?.predefinedOrderIDs?.[0]) {
        const predefinedId = autoFillData.order.predefinedOrderIDs[0];
        const predefinedOrders = control._defaultValues?.predefinedOrders || [];
        const selected = predefinedOrders.find(
          (p) => p?.id === predefinedId
        );
        if (selected?.id && selected?.name) {
          predefineDropDownRef.current?.setSelectedOption({
            label: selected.name,
            value: selected.id,
            data: {
              capitolId: selected.capitolId || '',
              subCapitolId: selected.subCapitolId || '',
              elementOrderId: selected.elementOrderId || '',
              activityId: selected.activityId || '',
            },
          });
          setValue('predefine', predefinedId);
        }
      }
    } catch (error) {
      Sentry.captureException(error);
      console.error('Error fetching data in NewWorkOrderForm:', error);
    }
  }, [autoFillData]);

  useEffect(() => {
    if (autoFillData?.order) {
      fetchToFillData();
    }
  }, [autoFillData, fetchToFillData]);

  // HandleSelect functions
  const onSelectConcession = useCallback(
    (
      option: OptionSelected | undefined,

      onChange: (...event: any[]) => void
    ) => {
      const tasks = async () => {
        const {
          data: { concession },
        } = await client.query({
          query: GET_CONCESSION_BY_ID,
          variables: { id: option?.value },
        });

        setValue('city', concession.location);

        const { readRoads } = client.readQuery({
          query: GET_ROADS_BY_IDS,
          variables: { ids: concession.roadIDs ?? [] },
        });

        let finalRoads = readRoads;
        if (readRoads.length === 0) {
          await fetchDefaultData();
          const result = client.readQuery({
            query: GET_ROADS_BY_IDS,
            variables: { ids: concession.roadIDs ?? [] },
          });
          finalRoads = result?.readRoads ?? [];
        }
        setValue('roads', finalRoads);

        // Autoseleccionar primera carretera
        if (finalRoads && finalRoads.length > 0) {
          const firstRoad = finalRoads[0];
          setValue('road', firstRoad.id);
          roadDropDownRef.current?.setSelectedOption({
            label: firstRoad.name,
            value: firstRoad.id,
          });
          setValue(
            'pkInitRoad',
            pkFormatter(firstRoad.pkInitKm, firstRoad.pkInitMeter, true)
          );
          setValue(
            'pkEndRoad',
            pkFormatter(firstRoad.pkEndKm, firstRoad.pkEndMeter, true)
          );
          setValue('roadDirections', firstRoad.directions);
        } else {
          roadDropDownRef.current?.clearSelected();
          setValue('road', '');
        }
      };
      onChange(option?.value ?? '');
      tasks().catch((err) => {
        Sentry.captureException(err);
        console.log(err);
      });
    },
    []
  );

  /**
   * Lo que el botón de autolocalizar PK escribe en el formulario.
   *
   * Vive aquí y no en el hook porque aquí están el `setValue` y la ref del
   * desplegable de carretera — que NO lee el valor del formulario, así que
   * hay que decírselo aparte o el operario ve la vía vieja seleccionada.
   */
  const applyAutolocatedPk = useCallback(
    (match: PkCandidate) => {
      // El mismo PK en los dos campos: la actuación es puntual. Si resulta ser
      // un tramo, el operario corrige el final.
      const pk = pkFormatter(match.pkKm, match.pkMeter, true);
      setValue('pkInit', pk);
      setValue('pkEnd', pk);

      if (match.roadId) {
        roadDropDownRef.current?.setSelectedOption({
          label: match.roadName,
          value: match.roadId,
        });
        setValue('road', match.roadId);
      }
      if (match.roadRange) {
        setValue(
          'pkInitRoad',
          pkFormatter(match.roadRange.pkInitKm, match.roadRange.pkInitMeter, true)
        );
        setValue(
          'pkEndRoad',
          pkFormatter(match.roadRange.pkEndKm, match.roadRange.pkEndMeter, true)
        );
      }
    },
    [setValue]
  );

  useEffect(() => {
    onRegisterAutolocate?.(applyAutolocatedPk);
    // Solo al montar: `applyAutolocatedPk` es estable (`setValue` de RHF lo es)
    // y volver a registrarlo en cada render no aportaría nada.
  }, []);

  const onSelectRoad = useCallback(
    (
      option: OptionSelected | undefined,

      onChange: (...event: any[]) => void
    ) => {
      const tasks = async () => {
        const {
          data: { road },
        } = await client.query({
          query: GET_ROAD_BY_ID,
          variables: { id: option?.value },
        });

        setValue(
          'pkInitRoad',
          pkFormatter(road.pkInitKm, road.pkInitMeter, true)
        );
        setValue('pkEndRoad', pkFormatter(road.pkEndKm, road.pkEndMeter, true));
        setValue('roadDirections', road.directions);

        onChange(option?.value ?? '');
      };
      tasks().catch((err) => {
        Sentry.captureException(err);
        console.log(err);
      });
    },
    []
  );

  // Replicar lógica de onSelectConcession para autoselección y carga de roads al editar
  useLayoutEffect(() => {
    const concessions = control._defaultValues?.concessions;
    const preselectedConcessionId = control._defaultValues?.concession;
    const preselectedRoadId = control._defaultValues?.road;
    const isEditing = !!preselectedConcessionId;

    // Función que replica EXACTAMENTE la lógica de onSelectConcession
    const loadConcessionData = async (
      concessionId: string,
      shouldClearRoad: boolean
    ) => {
      try {
        // 1. Query para obtener datos de la concesión
        const {
          data: { concession },
        } = await client.query({
          query: GET_CONCESSION_BY_ID,
          variables: { id: concessionId },
        });

        // 2. Establecer city
        setValue('city', concession.location);

        // 3. Leer roads del cache
        let readRoads = client.readQuery({
          query: GET_ROADS_BY_IDS,
          variables: { ids: concession.roadIDs ?? [] },
        })?.readRoads;

        // 4. Si no hay roads en cache, fetchear y releer
        if (!readRoads || readRoads.length === 0) {
          await fetchDefaultData();
          readRoads = client.readQuery({
            query: GET_ROADS_BY_IDS,
            variables: { ids: concession.roadIDs ?? [] },
          })?.readRoads;
        }

        // 5. Establecer roads y autoseleccionar primera carretera
        if (readRoads && readRoads.length > 0) {
          setValue('roads', readRoads);

          // 6. Autoseleccionar primera carretera si es creación nueva (shouldClearRoad=true)
          if (shouldClearRoad) {
            const firstRoad = readRoads[0];
            setValue('road', firstRoad.id);
            roadDropDownRef.current?.setSelectedOption({
              label: firstRoad.name,
              value: firstRoad.id,
            });
            // Cargar PKs y directions de la primera carretera
            setValue(
              'pkInitRoad',
              pkFormatter(firstRoad.pkInitKm, firstRoad.pkInitMeter, true)
            );
            setValue(
              'pkEndRoad',
              pkFormatter(firstRoad.pkEndKm, firstRoad.pkEndMeter, true)
            );
            setValue('roadDirections', firstRoad.directions);
          }
        } else if (shouldClearRoad) {
          // Si no hay roads, limpiar
          roadDropDownRef.current?.clearSelected();
          setValue('road', '');
        }

        // 7. Establecer el valor de concession en el formulario
        setValue('concession', concessionId);

        // 8. Si es edición y hay road preseleccionada, cargar sus directions
        if (!shouldClearRoad && preselectedRoadId) {
          const {
            data: { road },
          } = await client.query({
            query: GET_ROAD_BY_ID,
            variables: { id: preselectedRoadId },
          });
          setValue('roadDirections', road.directions);
        }
      } catch (err) {
        Sentry.captureException(err);
        console.log('Error loading concession data:', err);
      }
    };

    // CASO 1: Nueva orden con una sola concesión disponible
    if (!isEditing && concessions?.length === 1) {
      const concession = concessions[0];
      const name = concession?.name;
      const id = concession?.id;

      if (name && id && name !== 'CX') {
        // Autoseleccionar y limpiar road
        loadConcessionData(id, true);
      }
      return;
    }

    // CASO 2: Editando - cargar roads de la concesión preseleccionada
    if (isEditing) {
      // NO limpiar road porque ya está preseleccionada
      loadConcessionData(preselectedConcessionId, false);
    }
  }, []); // Dependencias vacías para ejecutar solo en mount

  const onSelectCapitol = useCallback(
    (
      option: OptionSelected | undefined,

      onChange: (...event: any[]) => void
    ) => {
      const tasks = async () => {
        const { readSubCapitols } = client.readQuery({
          query: GET_SUB_CAPITOLS_BY_IDS,
          variables: {
            ids: option?.data ?? [],
          },
        });

        if (readSubCapitols.length === 0) {
          await fetchDefaultData();

          const { readSubCapitols } = client.readQuery({
            query: GET_SUB_CAPITOLS_BY_IDS,
            variables: {
              ids: option?.data ?? [],
            },
          });
          const sortedSubCapitols = [...readSubCapitols].sort((a, b) =>
            a.name.localeCompare(b.name)
          );
          setValue('subCapitols', sortedSubCapitols);
        } else {
          const sortedSubCapitols = [...readSubCapitols].sort((a, b) =>
            a.name.localeCompare(b.name)
          );
          setValue('subCapitols', sortedSubCapitols);
        }
        subCapitolDropDownRef.current?.clearSelected();
        setValue('subCapitol', '');
        setValue('elementOrders', []);
        elementOrdersDropDownRef.current?.clearSelected();
        onChange(option?.value ?? '');
      };
      tasks().catch((err) => {
        Sentry.captureException(err);
        console.log(err);
      });
    },
    []
  );

  const onSelectPredefined = useCallback(
    (
      option: OptionSelected | undefined,

      onChange: (...event: any[]) => void
    ) => {
      const tasks = async () => {
        onChange(option?.value ?? '');

        const [
          {
            data: { capitol },
          },
          {
            data: { subCapitol },
          },
        ] = await Promise.all([
          client.query({
            query: GET_CAPITOL_BY_ID,
            variables: {
              id: option?.data.capitolId,
            },
          }),
          client.query({
            query: GET_SUB_CAPITOL_BY_ID,
            variables: {
              id: option?.data.subCapitolId,
            },
          }),
        ]);

        const { readSubCapitols } = client.readQuery({
          query: GET_SUB_CAPITOLS_BY_IDS,
          variables: {
            ids: capitol?.subCapitolsIDs ?? [],
          },
        });

        if (readSubCapitols.length === 0) {
          await fetchDefaultData();
          const { readSubCapitols } = client.readQuery({
            query: GET_SUB_CAPITOLS_BY_IDS,
            variables: {
              ids: capitol?.subCapitolsIDs ?? [],
            },
          });
          const sortedSubCapitols = [...readSubCapitols].sort((a, b) =>
            a.name.localeCompare(b.name)
          );
          setValue('subCapitols', sortedSubCapitols);
        } else {
          const sortedSubCapitols = [...readSubCapitols].sort((a, b) =>
            a.name.localeCompare(b.name)
          );
          setValue('subCapitols', sortedSubCapitols);
        }

        capitolDropDownRef.current?.setSelectedOption({
          label: capitol.name,
          value: capitol.id,
          data: capitol.subCapitolsIDs,
        });

        subCapitolDropDownRef.current?.setSelectedOption({
          label: subCapitol.name,
          value: subCapitol.id,
          data: subCapitol.elementOrdersIds,
        });
        if (isAction) {
          const {
            data: { elementOrder },
          } = await client.query({
            query: GET_ELEMENT_ORDER_BY_ID,
            variables: {
              id: option?.data.elementOrderId,
            },
          });

          const { readElementOrders } = client.readQuery({
            query: GET_ELEMENT_ORDERS_BY_ID,
            variables: {
              ids: subCapitol?.elementOrdersIds ?? [],
            },
          });
          const sortedElements = [...readElementOrders].sort((a, b) =>
            a.name.localeCompare(b.name)
          );
          setValue('elementOrders', sortedElements);

          elementOrdersDropDownRef.current?.setSelectedOption({
            label: elementOrder.name,
            value: elementOrder.id,
            data: elementOrder.subCapitolsIDs,
          });

          // Guard: Activity és opcional en el predefinido. Si no porta activityId,
          // deixar el camp buit (la validació `required` quan $isAction obliga a triar-ne una).
          if (option?.data.activityId) {
            const {
              data: { activity },
            } = await client.query({
              query: GET_ACTIVITY_BY_ID,
              variables: {
                id: option.data.activityId,
              },
            });
            activitysDropDownRef.current?.setSelectedOption({
              label: activity.name,
              value: activity.id,
            });
          } else {
            activitysDropDownRef.current?.clearSelected();
            setValue('actionActivity', '');
          }
        }
      };
      tasks().catch((err) => {
        Sentry.captureException(err);
        console.log(err);
      });
    },
    [
      capitolDropDownRef,
      subCapitolDropDownRef,
      elementOrdersDropDownRef,
      activitysDropDownRef,
    ]
  );

  const onSelectSubCapitol = useCallback(
    (
      option: OptionSelected | undefined,

      onChange: (...event: any[]) => void
    ) => {
      const tasks = async () => {
        if (isAction) {
          try {
            const { readElementOrders } = client.readQuery({
              query: GET_ELEMENT_ORDERS_BY_ID,
              variables: {
                ids: option?.data ?? [],
              },
            });
            if (readElementOrders.length === 0) {
              await fetchDefaultData();
              const { readElementOrders } = client.readQuery({
                query: GET_ELEMENT_ORDERS_BY_ID,
                variables: {
                  ids: option?.data ?? [],
                },
              });
              const sortedElements = [...readElementOrders].sort((a, b) =>
                a.name.localeCompare(b.name)
              );
              setValue('elementOrders', sortedElements);
            } else {
              const sortedElements = [...readElementOrders].sort((a, b) =>
                a.name.localeCompare(b.name)
              );
              setValue('elementOrders', sortedElements);
            }
            elementOrdersDropDownRef.current?.clearSelected();
          } catch (err) {
            Sentry.captureException(err);
            console.log(err);
          }
        }
        onChange(option?.value ?? '');
      };
      tasks().catch((err) => {
        Sentry.captureException(err);
        console.log(err);
      });
    },
    []
  );

  const inputSizeSupcapitol = isPortraitTable ? 'M' : 'L';

  const renderSubCapitols = useCallback<
    ControllerProps<WorkOrder, 'subCapitol'>['render']
  >(
    ({ field: { onChange }, formState: { defaultValues } }) => {
      const options =
        subCapitols?.map((subCapitol) => ({
          label: subCapitol?.name || '',
          value: subCapitol?.id || '',
          data: subCapitol?.elementOrdersIds || '',
        })) || [];
      return (
        <SearchableDropdown
          ref={subCapitolDropDownRef}
          id="subchapter-select"
          inputSize={inputSizeSupcapitol}
          onChangeSelected={(option) => onSelectSubCapitol(option, onChange)}
          typeDropDown="Default"
          labelText={t('workOrder.subchapter')}
          placeholder={t('common:select')}
          defaultValue={defaultValues?.subCapitol}
          borderError={!!errors.subCapitol}
          options={options}
        />
      );
    },
    [subCapitols, errors, inputSizeSupcapitol]
  );

  const renderTipusVial = (
    <Controller
      control={control}
      name="track"
      render={({ field: { onChange }, formState: { defaultValues } }) => {
        const options =
          translateTrackOptions(
            defaultValues?.groupTracks as CatalogEntry[] | undefined,
            t
          );
        return (
          <DropDownInput
            id="track-select"
            ref={trackDropDownRef}
            inputSize={'M'}
            onChangeSelected={(option) => onChange(option?.value ?? '')}
            typeDropDown="Default"
            labelText={t('workOrder.roadType')}
            placeholder={t('common:select')}
            borderError={!!errors.track}
            defaultValue={defaultValues?.track}
            options={options}
          />
        );
      }}
    />
  );

  const renderElementOrders = useCallback<
    ControllerProps<WorkOrder, 'actionElementOrder'>['render']
  >(
    ({ field: { onChange }, formState: { defaultValues } }) => {
      const options =
        elementOrders?.map((elementOrder) => ({
          label: elementOrder?.name || '',
          value: elementOrder?.id || '',
          data: elementOrder?.subCapitolIDs || '',
        })) || [];

      return (
        <SearchableDropdown
          id="element-order-select"
          ref={elementOrdersDropDownRef}
          labelText={t('workOrder.element')}
          placeholder={'Selecciona'}
          inputSize={'L'}
          options={options}
          typeDropDown={'Default'}
          defaultValue={defaultValues?.actionElementOrder}
          onChangeSelected={(option) => onChange(option?.value ?? '')}
          borderError={!!errors.actionElementOrder}
        />
      );
    },
    [elementOrders, errors]
  );

  return (
    <ContainerDescription>
      <Title component="h2" variant="semiBold">
        {location.pathname.split('/').includes('new-action')
          ? t('workOrder.description')
          : t('common:workOrder.workOrderDescription')}
      </Title>

      <InputsContainer>
        <Controller
          control={control}
          name="predefine"
          render={({ field: { onChange }, formState: { defaultValues } }) => {
            const options =
              defaultValues?.predefinedOrders?.map((predefinedOrder) => ({
                label: predefinedOrder?.name || '',
                value: predefinedOrder?.id || '',
                data: {
                  capitolId: predefinedOrder?.capitolId || '',
                  subCapitolId: predefinedOrder?.subCapitolId || '',
                  elementOrderId: predefinedOrder?.elementOrderId || '',
                  activityId: predefinedOrder?.activityId || '',
                },
              })) || [];
            return (
              <SearchableDropdown
                id="predefine-select"
                ref={predefineDropDownRef}
                inputSize={'XL'}
                onChangeSelected={(option) =>
                  onSelectPredefined(option, onChange)
                }
                typeDropDown="Default"
                labelText={t('workOrder.predefined')}
                placeholder={t('common:select')}
                defaultValue={defaultValues?.predefine}
                borderError={!!errors.predefine}
                options={options}
                minSearchLength={2}
              />
            );
          }}
        />
        <Controller
          control={control}
          name="capitol"
          render={({ field: { onChange }, formState: { defaultValues } }) => {
            const options =
              defaultValues?.capitols?.map((capitol) => ({
                label: capitol?.name || '',
                value: capitol?.id || '',
                data: capitol?.subCapitolsIDs || '',
              })) || [];
            return (
              <SearchableDropdown
                id="capitol-select"
                ref={capitolDropDownRef}
                inputSize={isPortraitTable ? 'M' : 'L'}
                onChangeSelected={(option) => onSelectCapitol(option, onChange)}
                typeDropDown="Default"
                labelText={t('workOrder.chapter')}
                placeholder={t('common:select')}
                defaultValue={defaultValues?.capitol}
                borderError={!!errors.capitol}
                options={options}
              />
            );
          }}
        />
        <Controller
          control={control}
          name="subCapitol"
          render={renderSubCapitols}
        />

        {isAction && (
          <>
            <Controller
              control={control}
              name="actionElementOrder"
              render={renderElementOrders}
            />

            <Controller
              name="actionActivity"
              control={control}
              render={({
                field: { onChange },
                formState: { defaultValues },
              }) => {
                const options =
                  defaultValues?.activitys?.map((activity) => ({
                    label: activity?.name || '',
                    value: activity?.id || '',
                  })) || [];
                return (
                  <SearchableDropdown
                    id="activity-select"
                    ref={activitysDropDownRef}
                    labelText={t('workOrder.action')}
                    placeholder={t('location.selectPlaceholder')}
                    inputSize={'L'}
                    options={options}
                    typeDropDown={'Default'}
                    onChangeSelected={(option) => onChange(option?.value ?? '')}
                    defaultValue={defaultValues?.actionActivity}
                    borderError={!!errors.actionActivity}
                  />
                );
              }}
            />
          </>
        )}

        {(isAction || isAccident) && (
          <Controller
            name="registeredByUserId"
            control={control}
            render={({ field: { onChange }, formState: { defaultValues } }) => {
              const options =
                defaultValues?.operators?.map((op) => ({
                  label: op?.name || '',
                  value: op?.id || '',
                })) || [];
              return (
                <SearchableDropdown
                  id="registered-by-select"
                  // La aclaración va PEGADA al texto de la etiqueta, no suelta
                  // al lado del desplegable: allí caía en la siguiente celda de
                  // la rejilla, lejos del campo que explica, y su burbuja tapaba
                  // el control de al lado. Dentro de la etiqueta sale hacia
                  // arriba y no estorba a nada.
                  labelText={
                    <>
                      {t('registeredBy.label')}
                      <HelpTooltip text={t('registeredBy.help')} />
                    </>
                  }
                  placeholder={t('registeredBy.placeholder')}
                  inputSize={'L'}
                  options={options}
                  typeDropDown={'Default'}
                  onChangeSelected={(option) => onChange(option?.value ?? '')}
                  defaultValue={defaultValues?.registeredByUserId}
                  borderError={!!errors.registeredByUserId}
                />
              );
            }}
          />
        )}

        {isAction || isAccident ? undefined : (
          <Controller
            control={control}
            name="classification"
            render={({ field: { onChange }, formState: { defaultValues } }) => {
              const options =
                defaultValues?.classifications?.map((classification) => ({
                  label: classification?.name || '',
                  value: classification?.id || '',
                })) || [];

              return (
                <DropDownInput
                  id="classification-select"
                  ref={classificationDropDownRef}
                  inputSize={'S'}
                  onChangeSelected={(option) => {
                    onChange(option?.value);
                  }}
                  typeDropDown="Default"
                  labelText={t('workOrder.classification')}
                  placeholder={t('common:select')}
                  defaultValue={defaultValues?.classification}
                  borderError={!!errors.classification}
                  options={options}
                  isDisabled={isEditForm}
                />
              );
            }}
          />
        )}
        <LocationInputGroupOrder>
          <ConcessionWrapper>
            <Controller
              control={control}
              name="concession"
              render={({
                field: { onChange, value },
                formState: { defaultValues },
              }) => {
                const options =
                  defaultValues?.concessions?.reduce((acc, curr) => {
                    if (curr?.name !== 'CX') {
                      acc.push({
                        label: curr?.name || '',
                        value: curr?.id || '',
                      });
                    }

                    return acc;
                  }, [] as GroupTypes[]) || [];

                // Si solo hay una concesión, auto-seleccionar y no mostrar nada
                if (options.length === 1) {
                  const concessionId = options[0].value;
                  if (value !== concessionId) {
                    onChange(concessionId);
                  }
                  return <></>;
                }

                // Si hay múltiples concesiones, mostrar dropdown
                return (
                  <DropDownInput
                    id="concession-select"
                    ref={concessionDropDownRef}
                    inputSize="XS"
                    onChangeSelected={(option) =>
                      onSelectConcession(option, onChange)
                    }
                    typeDropDown="Default"
                    labelText={t('user.concession')}
                    placeholder={t('common:select')}
                    defaultValue={defaultValues?.concession}
                    borderError={!!errors.concession}
                    options={options}
                  />
                );
              }}
            />
          </ConcessionWrapper>

          <InputText
            {...register('city')}
            id="city-input"
            inputSize="S"
            label={t('workOrder.location')}
            typeInput="normal"
            borderError={!city && !!errors.city}
            disabled
          />

          <RoadInputSubGroup>
            <Controller
              control={control}
              name="road"
              render={({
                field: { onChange },
                formState: { defaultValues },
              }) => {
                const options =
                  roads?.map((road) => ({
                    label: road?.name || '',
                    value: road?.id || '',
                  })) || [];

                return (
                  <DropDownInput
                    id="road-select"
                    inputSize={'XS'}
                    ref={roadDropDownRef}
                    onChangeSelected={(option) => {
                      onSelectRoad(option, onChange);
                    }}
                    typeDropDown="Default"
                    labelText={t('workOrder.road')}
                    placeholder={t('common:select')}
                    borderError={!!errors.road}
                    defaultValue={defaultValues?.road}
                    options={options}
                  />
                );
              }}
            />

            <InputText
              {...register('pkInitRoad')}
              id="pk-init-road-input"
              label={t('workOrder.start')}
              inputSize="12"
              typeInput="normal"
              placeholder="000 + 000"
              borderError={!pkInitRoad && !!errors.pkInitRoad}
              disabled
            />

            <InputText
              {...register('pkEndRoad')}
              id="pk-end-road-input"
              label={t('workOrder.end')}
              inputSize="12"
              typeInput="normal"
              placeholder="000 + 000"
              borderError={!pkEndRoad && !!errors.pkEndRoad}
              disabled
            />
          </RoadInputSubGroup>
        </LocationInputGroupOrder>

        <LocationInputGroupOrder>
          <InputText
            {...registerPk(register, setValue, 'pkInit')}
            id="pk-init-input"
            label={t('workOrder.pkInitial')}
            inputSize={isLandscapeTablet ? '15' : '16'}
            typeInput="normal"
            placeholder={t('common:pkExample')}
            borderError={!!errors.pkInit}
          />

          <InputText
            {...registerPk(register, setValue, 'pkEnd')}
            id="pk-end-input"
            label={t('workOrder.pkFinal')}
            inputSize={isLandscapeTablet ? '15' : '16'}
            typeInput="normal"
            placeholder={t('common:pkExample')}
            borderError={!!errors.pkEnd}
          />

          {renderTipusVial}
        </LocationInputGroupOrder>

        <LocationInputGroupOrder>
          <Controller
            control={control}
            name="direction"
            render={({
              field: { onChange, value },
              formState: { defaultValues },
            }) => {
              const defaultStatusValue = {
                [WorkOrderDirection.TOP]: 'top',
                [WorkOrderDirection.DOWN]: 'down',
                [WorkOrderDirection.BOTH]: 'both',
                [WorkOrderDirection.UNSPECIFIC]: UNSPECIFIC,
              };

              const options: PartialDirection = {
                top: WorkOrderDirection.TOP,
                down: WorkOrderDirection.DOWN,
                both: WorkOrderDirection.BOTH,
                [UNSPECIFIC]: UNSPECIFIC,
              };

              return (
                <ArrowRadioGroup
                  directions={['down', 'both', 'top', UNSPECIFIC]}
                  hasUnspecific
                  name="direction"
                  label={t('common:workOrder.direction')}
                  defaultValue={
                    value
                      ? value
                      : defaultStatusValue[
                      defaultValues?.direction as WorkOrderDirection
                      ]
                  }
                  borderError={!!errors.direction}
                  onChangeDirection={(dir) => {
                    onChange(options[dir]);
                  }}
                  directionLabels={{
                    down: roadDirections?.[0],
                    top: roadDirections?.[roadDirections?.length - 1],
                  }}
                />
              );
            }}
          />

          {showMargin && (
            <Controller
              control={control}
              name="margin"
              render={({
                field: { onChange, value },
                formState: { defaultValues },
              }) => {
                const defaultStatusValue: PartialDirection = {
                  [Margin.LEFT]: 'left',
                  [Margin.BOTH]: 'both',
                  [Margin.RIGHT]: 'right',
                  [Margin.UNSPECIFIC]: UNSPECIFIC,
                };

                const options: PartialDirection = {
                  left: Margin.LEFT,
                  both: Margin.BOTH,
                  right: Margin.RIGHT,
                  [UNSPECIFIC]: UNSPECIFIC,
                };

                return (
                  <ArrowRadioGroup
                    directions={['left', 'both', 'right', UNSPECIFIC]}
                    hasUnspecific
                    name="margin"
                    label={t('common:workOrder.margin')}
                    defaultValue={
                      value
                        ? value
                        : defaultStatusValue[defaultValues?.margin as Margin]
                    }
                    borderError={!!errors.margin}
                    onChangeDirection={(dir) => {
                      onChange(options[dir]);
                    }}
                  />
                );
              }}
            />
          )}
        </LocationInputGroupOrder>

        <LocationInputGroupOrder>
          <Controller
            control={control}
            name="lane"
            render={({ field: { onChange }, formState: { defaultValues } }) => {
              const options =
                translateLaneOptions(
                  defaultValues?.groupLanes as CatalogEntry[] | undefined,
                  t
                );
              return (
                <DropDownInput
                  id="lane-select"
                  ref={laneDropDownRef}
                  inputSize={'S'}
                  onChangeSelected={(option) => onChange(option?.value)}
                  typeDropDown="Default"
                  labelText={t('workOrder.lane')}
                  placeholder={t('common:labels.select')}
                  borderError={!!errors.lane}
                  defaultValue={defaultValues?.lane}
                  options={options}
                />
              );
            }}
          />

          {!isAnAction && !isAnAccident && (
            <Controller
              control={control}
              name="teamRelationships"
              render={({ field: { onChange, value }, formState: { defaultValues, errors } }) => {
                const options =
                  (defaultValues?.teams as any[])?.map((team) => ({
                    label: team?.name || '',
                    value: team?.id || '',
                  })) || [];
                const selectedValues = Array.isArray(value) ? value : (value ? [value as string] : []);
                if (options.length === 0) {
                  return (
                    <MultiSelectDropdown
                      inputSize="L"
                      typeDropDown="Default"
                      labelText={t('workOrder.team')}
                      placeholder={t('common:select')}
                      options={[]}
                      selectedValues={[]}
                      onChangeSelected={() => undefined}
                      isDisabled={true}
                    />
                  );
                }
                return (
                  <MultiSelectDropdown
                    inputSize="L"
                    typeDropDown="Default"
                    labelText={t('workOrder.team')}
                    placeholder={t('common:select')}
                    options={options}
                    selectedValues={selectedValues}
                    onChangeSelected={(ids) => onChange(ids)}
                    borderError={!!errors?.teamRelationships}
                    error={errors?.teamRelationships?.message as string | undefined}
                  />
                );
              }}
            />
          )}

          {!isAnAction && !isAnAccident && (
            <ContainerCampaign>
              <ContainerSwitch>
                {!isEditForm && (
                  <SimpleSwitch
                    disabled={isAccident as boolean}
                    state={switchState as boolean}
                    changeState={setSwitchState as () => boolean}
                  />
                )}
                <StyledTypography size="S">
                  {t('workOrder.campaign')}
                </StyledTypography>
              </ContainerSwitch>
              <InputText
                {...register('campaign')}
                disabled={!switchState}
                inputSize="35"
                typeInput="normal"
                placeholder={t('common:labels.name')}
                borderError={!!errors.campaign}
              />
            </ContainerCampaign>
          )}
        </LocationInputGroupOrder>
      </InputsContainer>
      <InputTextAreaStyled
        {...register('description', {})}
        id="description-input"
        name="description"
        disabled={false}
        label={t('workOrder.description')}
        placeholder={t('placeholders.description')}
        borderError={!!errors.description}
        error={errors.description?.message}
      />
      <InputTextAreaStyled
        {...register('notes', {})}
        id="notes-input"
        name="notes"
        disabled={false}
        label={t('workOrder.notes')}
        placeholder={t('placeholders.notes')}
        borderError={!!errors.notes}
        error={errors.notes?.message}
      />
    </ContainerDescription>
  );
};
